import { useCallback, useEffect, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import type { SharePointExternalRef } from "@repancy/core";
import type { DocumentType, ConfidentialityLevel } from "@repancy/core";
import { testIds } from "../../lib/testIds";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useAppDispatch } from "../../store";
import { useWindowContext } from "../desktop/WindowContext";
import { dispatchPromptLabPrefill, PROMPT_LAB_PATH } from "../../lib/promptLab/sendDocToPromptLab";
import { VersionHistoryDialog } from "./VersionHistoryDialog";
import {
  DocumentProvenance,
  type ProvenanceSource,
} from "./DocumentProvenance";
import { RemoteDriveItemPreview } from "./RemoteDriveItemPreview";
import { RemoteFolderPreview } from "./RemoteFolderPreview";
import { PreviewTipTapEditor } from "./PreviewTipTapEditor";
import { saveBack } from "../../lib/graph/saveBack";
import { HelpTip } from "../ui/HelpTip";
import type { PreviewSource } from "../../lib/sharepoint/previewSource";
import { useResolvedPortalSlug } from "../../lib/tenant/portalSlugFromUrl";

/**
 * K.3.a — Proof-of-SharePoint check.
 *
 * Fixture docs (mockDocsFull) have no real SharePoint backing — they live
 * only in repo source. Real docs that flowed in from `lib/graph/syncDrive`
 * carry an `externalRef.webUrl` of the form
 * `https://<tenant>.sharepoint.com/sites/<site>/_layouts/...`. The button
 * activates only when the URL is BOTH non-empty AND a real
 * `*.sharepoint.com` host so a stub seed (e.g. `https://example.com/foo`)
 * doesn't claim to be SharePoint when it isn't.
 *
 * Exported so the K.3 vitest can pin the contract.
 */
export function isRealSharePointWebUrl(webUrl: string | null | undefined): boolean {
  if (typeof webUrl !== 'string') return false;
  const trimmed = webUrl.trim();
  if (trimmed.length === 0) return false;
  // We accept any sharepoint.com tenant, not just areweai.sharepoint.com,
  // because customer portals may resolve to their own tenant after H.x.
  return /^https:\/\/[a-z0-9-]+\.sharepoint\.com\//i.test(trimmed);
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

interface MockDocFull {
  _id: string;
  // 2026-05-27 — matterId on the projection so callers (Send-to-Prompt-Lab,
  // future move/copy actions) can build a real Document entity instead
  // of inheriting an empty string. Live rows always carry it from
  // `api.documents.getDocument`; legacy fixture rows below omit it
  // (the union of `matterId | undefined` is handled by callers via
  // `?? ''`).
  matterId?: string;
  title: string;
  documentType: string;
  confidentialityLevel: string;
  responsibleAttorneyId: string;
  currentVersion: number;
  modifiedAt: string;
  content: string;
  // Phase E — provenance fields (populated when live; undefined for fixtures).
  source?: "local" | "sharepoint";
  sharePointWebUrl?: string;
  // Phase A.1 (2026-05-27) — anonymous Graph share-link URL minted at
  // provision-time by `mintFixtureShareLinksForSlug`. When present,
  // `OpenInSharePointButton` opens this URL instead of the raw
  // driveItem webUrl, so non-tenant-member users don't bounce to
  // AccessDenied.aspx. Cached for 7 days; re-mints on expiry via the
  // existing `createDocumentShareLink` SPA action.
  publicShareUrl?: string;
  // Full SP back-reference. Present when the live row was synced from
  // SharePoint and we have a writable handle. Drives the inline TipTap
  // editor + save-back wiring below.
  externalRef?: SharePointExternalRef;
}

const ATTORNEY_NAMES: Record<string, string> = {
  "atty-1": "Sarah Chen",
  "atty-2": "Marcus Webb",
  "atty-3": "Diana Lopez",
};

// Heuristic: detect content that's clearly not safe to edit inline as
// markdown/text. Right now this is conservative — any non-null byte
// outside the printable+whitespace range disqualifies. Word/Excel doc
// bodies aren't stored as raw bytes in `documents.content` today, but
// this guards against future ingest paths that might.
function looksBinaryEncoded(content: string): boolean {
  if (content.length === 0) return false;
  const sample = content.slice(0, 2048);
  // \x00 is the cheapest tell for binary.
  if (sample.includes('\x00')) return true;
  return false;
}

const CONFIDENTIALITY_COLORS: Record<string, string> = {
  Public: "#008000",
  Confidential: "#FF8C00",
  AttorneyClientPrivileged: "#CC0000",
  WorkProduct: "var(--xp-selection)",
};

const mockDocsFull: Record<string, MockDocFull> = {
  d1: { _id: "d1", title: "Draft Purchase Agreement v3", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 3, modifiedAt: "2026-05-10T14:30:00Z", content: "ASSET PURCHASE AGREEMENT\n\nThis Asset Purchase Agreement (\"Agreement\") is entered into as of May 10, 2026, by and between Johnson Industries, Inc. (\"Seller\") and [Buyer Name] (\"Buyer\").\n\nRECITALS\n\nWHEREAS, Seller desires to sell, and Buyer desires to purchase, substantially all of the assets of Seller's manufacturing division, subject to the terms and conditions set forth herein;\n\nWHEREAS, the Board of Directors of each party has approved the transactions contemplated by this Agreement;\n\nNOW, THEREFORE, in consideration of the mutual covenants and agreements set forth herein, the parties agree as follows:\n\n1. DEFINITIONS\n   1.1 \"Acquired Assets\" means all of the assets, properties, and rights of every kind...\n   1.2 \"Assumed Liabilities\" means those liabilities expressly assumed by Buyer...\n   1.3 \"Closing Date\" means the date on which the Closing occurs...\n\n2. PURCHASE AND SALE\n   2.1 Purchase Price: $47,500,000 (Forty-Seven Million Five Hundred Thousand Dollars)\n   2.2 Payment Terms: Wire transfer at Closing, subject to working capital adjustment..." },
  d2: { _id: "d2", title: "Due Diligence Checklist", documentType: "Memorandum", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-2", currentVersion: 1, modifiedAt: "2026-05-08T09:15:00Z", content: "DUE DILIGENCE CHECKLIST\nJohnson Industries - Asset Purchase\n\n1. Corporate Documents\n   [ ] Certificate of Incorporation / Articles of Organization\n   [ ] Bylaws / Operating Agreement\n   [ ] Board Resolutions authorizing the transaction\n   [ ] Good Standing Certificates\n\n2. Financial Information\n   [x] Audited financials (3 years)\n   [x] Interim unaudited financials (current year)\n   [ ] Tax returns (3 years)\n   [ ] Accounts receivable aging\n\n3. Material Contracts\n   [x] Customer contracts (top 20)\n   [ ] Supplier agreements\n   [ ] Lease agreements\n   [ ] Employment agreements (key personnel)" },
  d3: { _id: "d3", title: "Target Company Financials Summary", documentType: "Brief", confidentialityLevel: "WorkProduct", responsibleAttorneyId: "atty-1", currentVersion: 2, modifiedAt: "2026-05-05T16:45:00Z", content: "ATTORNEY WORK PRODUCT - PRIVILEGED AND CONFIDENTIAL\n\nFinancial Summary: Johnson Industries Manufacturing Division\n\nRevenue (FY 2025): $82.3M\nEBITDA: $12.1M (14.7% margin)\nNet Assets: $34.2M\n\nKey Observations:\n- Revenue has grown 8% CAGR over 3 years\n- Customer concentration risk: top 5 customers = 62% of revenue\n- Deferred maintenance on Plant #3 estimated at $2.8M\n- Pending EPA remediation at former site (est. $500K-$1.2M)" },
  d8: { _id: "d8", title: "Term Sheet - Series B", documentType: "Contract", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-2", currentVersion: 5, modifiedAt: "2026-05-12T15:00:00Z", content: "TERM SHEET\nSeries B Preferred Stock Financing\nTechStart Inc.\n\nOffering: Series B Preferred Stock\nAmount: $25,000,000\nPre-Money Valuation: $75,000,000\nLead Investor: Pacific Ventures Fund III\n\nKey Terms:\n- Liquidation Preference: 1x non-participating\n- Anti-dilution: Broad-based weighted average\n- Board Composition: 2 Founders, 2 Investors, 1 Independent\n- Protective Provisions: Standard NVCA terms\n- Vesting: 4-year vesting, 1-year cliff for new grants" },
  d11: { _id: "d11", title: "Limited Partnership Agreement", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 3, modifiedAt: "2026-05-13T11:30:00Z", content: "LIMITED PARTNERSHIP AGREEMENT\nPACIFIC VENTURES FUND IV, L.P.\n\nThis Limited Partnership Agreement is entered into as of May 1, 2026...\n\n1. FORMATION AND NAME\n   The name of the Partnership shall be Pacific Ventures Fund IV, L.P.\n\n2. PURPOSE\n   The purpose of the Partnership is to make venture capital investments...\n\n3. CAPITAL COMMITMENTS\n   Total Fund Size: $200,000,000\n   General Partner Commitment: 2% ($4,000,000)\n   Management Fee: 2% of committed capital during investment period\n   Carried Interest: 20% over 8% preferred return" },
  d14: { _id: "d14", title: "Merger Agreement Draft", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 2, modifiedAt: "2026-05-13T09:00:00Z", content: "AGREEMENT AND PLAN OF MERGER\n\nThis Agreement and Plan of Merger (\"Merger Agreement\") dated as of May 13, 2026...\n\nBetween:\n- Pacific Ventures Acquisition Corp (\"Acquiror\")\n- [Target Company] (\"Target\")\n\n1. THE MERGER\n   1.1 At the Effective Time, Target shall be merged with and into Acquiror...\n   1.2 Merger Consideration: $3.50 per share in cash\n   1.3 Total Transaction Value: approximately $142,000,000" },
  d16: { _id: "d16", title: "Motion to Compel Discovery", documentType: "Motion", confidentialityLevel: "Public", responsibleAttorneyId: "atty-1", currentVersion: 1, modifiedAt: "2026-05-04T10:00:00Z", content: "IN THE SUPERIOR COURT OF THE STATE OF CALIFORNIA\nCOUNTY OF LOS ANGELES\n\nRIVERA FAMILY TRUST,\n  Plaintiff,\nv.\nJOHN DOE, as Trustee,\n  Defendant.\n\nCase No. BC-2026-12345\n\nMOTION TO COMPEL DISCOVERY\n\nPlaintiff Rivera Family Trust hereby moves this Court for an order compelling Defendant to respond to Plaintiff's First Set of Interrogatories and Request for Production of Documents, served on March 15, 2026..." },
};

// Fallback for docs not in the full map
function getDocData(id: string): MockDocFull | null {
  return mockDocsFull[id] ?? null;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentPreviewProps {
  documentId: string | null;
  // K.8 — optional explicit preview source. When kind is 'remote-driveItem',
  // RemoteDriveItemPreview takes over (fetches via the Node action). When
  // omitted or kind is 'convex', the existing matter-doc path renders from
  // `documentId`. Backwards-compatible: existing call sites without
  // `previewSource` keep their behavior unchanged.
  previewSource?: PreviewSource | null;
}

const FONT = "var(--xp-font-body)";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentPreview({ documentId, previewSource }: DocumentPreviewProps) {
  // K.8 — short-circuit to the connected-site preview component when the
  // selection is a remote driveItem. RemoteDriveItemPreview owns its own
  // chrome (title bar + "Open in SharePoint") so we return it directly.
  if (previewSource !== undefined && previewSource !== null && previewSource.kind === 'remote-driveItem') {
    return (
      <RemoteDriveItemPreview
        siteId={previewSource.siteId}
        driveId={previewSource.driveId}
        itemId={previewSource.itemId}
        name={previewSource.name}
        webUrl={previewSource.webUrl}
      />
    );
  }

  // K.9.8 — folder ROW click (not the expand toggle) selects a folder
  // for the preview pane. RemoteFolderPreview shows name + child counts
  // + Open-in-SharePoint link.
  if (previewSource !== undefined && previewSource !== null && previewSource.kind === 'remote-folder') {
    return (
      <RemoteFolderPreview
        siteId={previewSource.siteId}
        driveId={previewSource.driveId}
        folderId={previewSource.folderId}
        name={previewSource.name}
        webUrl={previewSource.webUrl}
      />
    );
  }

  // Convex query for real ids; fall back to fixture data for mock ids ("d1"…).
  //
  const isRealId = !!documentId && documentId.length > 8 && !/^d\d+$/.test(documentId);
  // Phase H — Send-to-Prompt-Lab + Open Version History wiring (Q11).
  const dispatch = useAppDispatch();
  const windowContext = useWindowContext();
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);

  const live = useQuery(
    api.documents.getDocument,
    isRealId ? { documentId: documentId as Id<'documents'> } : 'skip',
  );

  // F18.G.4 — Lazy hydrate from Graph. Seeded Sample-workspace docs
  // land with `content: ''` and an externalRef pointing at the real
  // SharePoint driveItem. Fire the action once per documentId; the
  // useQuery above reactively re-renders when the patch lands.
  const hydrateBlankDoc = useAction(api.autoProvisionedPreviewNode.hydrateBlankDoc);
  // F-DOCX-ROUNDTRIP-WIRING Phase 2 — fire sidecar extraction on
  // first preview-mount for docx-backed docs. Idempotent server-side.
  const extractDocxSidecarsFromDocument = useAction(
    api.docxFidelityNode.extractDocxSidecarsFromDocument,
  );
  // Phase 6.5 — anonymous share-link generator. Single-shot; result
  // lives in component state until the user dismisses the panel.
  const createDocumentShareLink = useAction(
    api.sharePointPublicLinks.createDocumentShareLink,
  );
  const [shareResult, setShareResult] = useState<
    | { kind: 'idle' }
    | { kind: 'pending' }
    | { kind: 'ok'; webUrl: string; expiresAt: string; scope: 'anonymous' | 'organization'; cached?: true }
    | { kind: 'error'; reason: string }
  >({ kind: 'idle' });
  const [shareCopied, setShareCopied] = useState(false);
  // 2026-05-28 — useResolvedPortalSlug (context-or-URL) so share-link
  // + save paths still see the portal when this preview is rendered
  // by the OUTER WindowLayer (outside <TenantBrandingProvider>).
  // The previous useOptionalTenantBranding read silently returned
  // null in that scenario, surfacing "No portal context for this
  // preview" even when the URL clearly named a portal.
  const portalSlug = useResolvedPortalSlug();

  // Phase D — docx sidecars (comments, revisions, footnotes, etc.)
  // for the Tiptap overlay. Empty bundle if the doc isn't a docx
  // origin or has no extracted sidecars yet.
  const sidecarBundle = useQuery(
    api.docxFidelity.getSidecarBundle,
    isRealId
      ? { documentId: documentId as Id<'documents'> }
      : 'skip',
  );
  const [footnoteDrawerOpen, setFootnoteDrawerOpen] = useState<{
    open: boolean;
    content: string;
    noteNumber: number;
  }>({ open: false, content: '', noteNumber: 0 });
  const hydrateAttemptedRef = useRef<Set<string>>(new Set());
  const [hydrating, setHydrating] = useState(false);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  useEffect(() => {
    if (!isRealId) return;
    if (!live) return;
    if (live.content !== '') return;
    if (!(live as { externalRef?: SharePointExternalRef }).externalRef) return;
    if (!portalSlug) return;
    const liveId = String(live._id);
    if (hydrateAttemptedRef.current.has(liveId)) return;
    hydrateAttemptedRef.current.add(liveId);
    setHydrating(true);
    setHydrateError(null);
    void hydrateBlankDoc({
      clientSlug: portalSlug,
      documentId: live._id as Id<'documents'>,
    })
      .then((r) => {
        if (!r.ok) {
          setHydrateError(
            'reason' in r ? `Could not load from SharePoint: ${r.reason}` : null,
          );
        }
      })
      .catch((err) => {
        setHydrateError(
          err instanceof Error ? err.message : String(err),
        );
      })
      .finally(() => {
        setHydrating(false);
      });
  }, [isRealId, live, portalSlug, hydrateBlankDoc]);

  // F-DOCX-ROUNDTRIP-WIRING Phase 2 — lazy sidecar extraction.
  // Fire-and-forget on first mount of any live doc whose externalRef
  // looks like a docx. The action is idempotent server-side
  // (`already-extracted` skip-reason on retry). State here is just to
  // surface an extraction-error banner per Phase 4 UX polish.
  const sidecarExtractAttemptedRef = useRef<Set<string>>(new Set());
  const [sidecarExtractError, setSidecarExtractError] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!isRealId) return;
    if (!live) return;
    const liveExternalRef = (live as { externalRef?: SharePointExternalRef })
      .externalRef;
    if (liveExternalRef === undefined) return;
    const webUrl = liveExternalRef.webUrl ?? '';
    const titleLower = (live.title ?? '').toLowerCase();
    const looksDocx =
      webUrl.toLowerCase().endsWith('.docx') || titleLower.endsWith('.docx');
    if (!looksDocx) return;
    const liveId = String(live._id);
    if (sidecarExtractAttemptedRef.current.has(liveId)) return;
    sidecarExtractAttemptedRef.current.add(liveId);
    setSidecarExtractError(null);
    void extractDocxSidecarsFromDocument({
      documentId: live._id as Id<'documents'>,
    })
      .then((r) => {
        // `skipped:true` with `reason: 'already-extracted'` is normal
        // on every re-mount; only surface unexpected reasons.
        if (
          r.skipped === true &&
          r.reason !== 'already-extracted' &&
          r.reason !== 'no-version'
        ) {
          setSidecarExtractError(
            r.detail ? `${r.reason}: ${r.detail}` : r.reason,
          );
        }
      })
      .catch((err) => {
        setSidecarExtractError(err instanceof Error ? err.message : String(err));
      });
  }, [isRealId, live, extractDocxSidecarsFromDocument]);

  // Save-back wiring for matter docs that mirror a SharePoint file.
  // Hooks must be unconditional, so they're declared here regardless of
  // whether the current doc is editable; gating happens via `canEdit`
  // computed below.
  const updateExternalEtag = useMutation(api.documents.updateExternalEtag);
  const [draft, setDraft] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const doc: MockDocFull | null = live
    ? {
        _id: live._id as unknown as string,
        matterId: live.matterId as unknown as string,
        title: live.title,
        documentType: live.documentType,
        confidentialityLevel: live.confidentialityLevel,
        responsibleAttorneyId:
          (live as { responsibleAttorneyId?: string }).responsibleAttorneyId ?? '',
        currentVersion: live.currentVersion,
        modifiedAt: live.modifiedAt,
        content: live.content,
        // Phase E — pull provenance from the live row when present.
        source: (live as { source?: 'local' | 'sharepoint' }).source,
        sharePointWebUrl:
          (live as { externalRef?: { webUrl?: string } }).externalRef
            ?.webUrl,
        // Phase A.1 (2026-05-27) — surface the provision-time
        // anonymous share-link so `OpenInSharePointButton` can prefer
        // it over the raw driveItem webUrl.
        publicShareUrl: (live as { publicShareUrl?: string }).publicShareUrl,
        externalRef: (
          live as { externalRef?: SharePointExternalRef }
        ).externalRef,
      }
    : documentId
      ? getDocData(documentId)
      : null;

  // Editable if (a) the live row has a SharePoint externalRef so we have
  // a writable handle, and (b) the content looks markdown/plain-textish.
  // Binary/word content is deferred to F-DOCX-EDIT.
  const externalRef = doc?.externalRef ?? null;
  const docContent = doc?.content ?? '';
  const canEdit =
    externalRef !== null &&
    !looksBinaryEncoded(docContent);

  // Seed draft + baseline whenever the source content changes. Effects
  // must run unconditionally — we no-op when there's nothing editable.
  useEffect(() => {
    if (!canEdit) {
      setDraft(null);
      setBaseline(null);
      return;
    }
    setDraft(docContent);
    setBaseline(docContent);
    setSaveError(null);
    setSaveSuccess(false);
    // We intentionally key by externalRef.itemId + content snapshot so a
    // selection change resets state. saveError/saveSuccess flips are
    // intentional resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, externalRef?.itemId, docContent]);

  const isDirty =
    canEdit && draft !== null && baseline !== null && draft !== baseline;

  // 2026-05-22 — when this document belongs to the current
  // auto-provisioned portal, save via the server-side app-only path
  // (no MSAL). Routing key: `portalSlug` is set when we're inside
  // a `/c/<slug>` subtree AND the externalRef's siteId matches one
  // locked to that portal. Server re-validates ownership.
  const saveAutoProvisioned = useAction(
    api.autoProvisionedPreviewNode.saveAutoProvisionedItem,
  );
  // F-DOCX-ROUNDTRIP-WIRING Phase 3 — sidecar-preserving docx
  // save-back (server rebuilds the zip via exportDocxForDocument).
  const saveAutoProvisionedDocx = useAction(
    api.autoProvisionedPreviewNode.saveAutoProvisionedDocxItem,
  );

  const handleCreatePublicShare = useCallback(async (): Promise<void> => {
    if (doc === null || doc === undefined) {
      setShareResult({ kind: 'error', reason: 'No document loaded.' });
      return;
    }
    if (!portalSlug) {
      setShareResult({ kind: 'error', reason: 'No portal context for this preview.' });
      return;
    }
    setShareResult({ kind: 'pending' });
    setShareCopied(false);
    try {
      const r = await createDocumentShareLink({
        clientSlug: portalSlug,
        documentId: doc._id as Id<'documents'>,
      });
      if (r.ok) {
        setShareResult({
          kind: 'ok',
          webUrl: r.webUrl,
          expiresAt: r.expiresAt,
          scope: r.scope,
          ...(r.cached ? { cached: true as const } : {}),
        });
      } else {
        setShareResult({ kind: 'error', reason: r.reason });
      }
    } catch (err) {
      setShareResult({
        kind: 'error',
        reason: err instanceof Error ? err.message : String(err),
      });
    }
  }, [portalSlug, createDocumentShareLink, doc]);

  const handleCopyShareUrl = useCallback(async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Clipboard blocked (e.g. cross-origin frame). Fall back to a
      // select-all hint by re-opening the URL in a new tab so the
      // user can copy from there.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!canEdit || !isDirty || draft === null || externalRef === null || !doc)
      return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    // F-DOCX-ROUNDTRIP-WIRING Phase 3 — detect docx by webUrl/title
    // extension so the docx branch picks up sidecar-preserving save.
    const webUrl = externalRef.webUrl ?? '';
    const looksDocx =
      webUrl.toLowerCase().endsWith('.docx') ||
      (doc.title ?? '').toLowerCase().endsWith('.docx');
    try {
      // Auto-provisioned portal: server-side app-only PUT, no MSAL.
      if (portalSlug) {
        // Docx branch: server rebuilds the zip via
        // exportDocxForDocument so comments / revisions / footnotes /
        // images survive the round-trip.
        if (looksDocx) {
          const r = await saveAutoProvisionedDocx({
            clientSlug: portalSlug,
            siteId: externalRef.siteId,
            driveId: externalRef.driveId,
            itemId: externalRef.itemId,
            documentId: doc._id as unknown as Id<'documents'>,
            expectedCTag: externalRef.etag ?? '',
          });
          if (r.ok === true) {
            setBaseline(draft);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            try {
              await updateExternalEtag({
                documentId: doc._id as unknown as Id<'documents'>,
                newEtag: r.cTag ?? r.eTag ?? '',
              });
            } catch {
              /* best-effort */
            }
          } else if (r.ok === false && r.reason === 'conflict') {
            setSaveError(
              'Someone else updated this file. Reload to see the latest, or save again to overwrite.',
            );
          } else {
            setSaveError(
              'message' in r ? `Save failed: ${r.message}` : `Save failed: ${r.reason}`,
            );
          }
          return;
        }
        const r = await saveAutoProvisioned({
          clientSlug: portalSlug,
          siteId: externalRef.siteId,
          driveId: externalRef.driveId,
          itemId: externalRef.itemId,
          content: draft,
          expectedCTag: externalRef.etag ?? '',
        });
        if (r.ok === true) {
          setBaseline(draft);
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
          try {
            await updateExternalEtag({
              documentId: doc._id as unknown as Id<'documents'>,
              newEtag: r.cTag ?? r.eTag ?? '',
            });
          } catch {
            // Best-effort.
          }
        } else if (r.ok === false && r.reason === 'conflict') {
          setSaveError(
            'Someone else updated this file. Reload to see the latest, or save again to overwrite.',
          );
        } else {
          setSaveError(
            'message' in r ? `Save failed: ${r.message}` : `Save failed: ${r.reason}`,
          );
        }
        return;
      }
      // Legacy MSAL-delegated save path (BYO connected sites + classic sync).
      const r = await saveBack(externalRef, draft);
      if (r.kind === 'ok') {
        setBaseline(draft);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        try {
          await updateExternalEtag({
            documentId: doc._id as unknown as Id<'documents'>,
            newEtag: r.newEtag,
          });
        } catch {
          // Best-effort — the SP write succeeded; etag patch failure
          // will just trigger a 412 next save, which the conflict
          // dialog handles.
        }
      } else if (r.kind === 'conflict') {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('repancy:save-back-conflict', {
              detail: {
                externalRef,
                localContent: draft,
                remoteEtag: r.remoteEtag,
                documentTitle: doc.title,
              },
            }),
          );
        }
        setSaveError(
          'Someone else edited this file since you opened it. Choose Overwrite to keep yours, Keep theirs to reload, or Save-as-new-local.',
        );
      } else {
        setSaveError(r.message);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [
    canEdit,
    isDirty,
    draft,
    externalRef,
    doc,
    updateExternalEtag,
    portalSlug,
    saveAutoProvisioned,
    saveAutoProvisionedDocx,
  ]);

  const handleDiscard = useCallback((): void => {
    if (baseline !== null) {
      setDraft(baseline);
      setSaveError(null);
    }
  }, [baseline]);

  // Listen for ConflictDialogHost's resolution so an 'overwrite' win
  // clears the dirty marker. Mirrors RemoteDriveItemPreview's effect.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (externalRef === null) return;
    const handler = (ev: Event) => {
      const detail = (
        ev as CustomEvent<{
          externalRef: { driveId: string; itemId: string };
          choice: 'overwrite' | 'keep-theirs' | 'save-as-new-local';
          result: { kind: string; newEtag?: string };
        }>
      ).detail;
      if (!detail) return;
      if (
        detail.externalRef.driveId !== externalRef.driveId ||
        detail.externalRef.itemId !== externalRef.itemId
      ) {
        return;
      }
      if (detail.choice === 'overwrite' && detail.result.kind === 'ok') {
        setBaseline(draft);
        setSaveError(null);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        if (detail.result.newEtag && doc) {
          void updateExternalEtag({
            documentId: doc._id as unknown as Id<'documents'>,
            newEtag: detail.result.newEtag,
          }).catch(() => {});
        }
      }
    };
    window.addEventListener('repancy:save-back-conflict-resolved', handler);
    return () => {
      window.removeEventListener('repancy:save-back-conflict-resolved', handler);
    };
  }, [externalRef, draft, doc, updateExternalEtag]);

  if (!doc) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f0f0f0",
          borderStyle: "solid",
          borderWidth: 2,
          fontFamily: FONT,
          fontSize: 12,
          color: "#404040",
          padding: 12,
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: 4, color: "#000" }}>
          Your SharePoint document library
        </div>
        <div>
          This is your portal's SharePoint site. Pick a document on the
          left to preview it. Edits save back to SharePoint.
        </div>
      </div>
    );
  }

  const confColor = CONFIDENTIALITY_COLORS[doc.confidentialityLevel] ?? "#000";
  const confLabel = doc.confidentialityLevel === "AttorneyClientPrivileged" ? "Atty-Client Privileged" : doc.confidentialityLevel;
  const attorney = ATTORNEY_NAMES[doc.responsibleAttorneyId] ?? doc.responsibleAttorneyId;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#f0f0f0",
        borderStyle: "solid",
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        overflow: "hidden",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 8px",
          background: "#ece9d8",
          borderBottom: "1px solid #808080",
          flexShrink: 0,
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span data-testid={testIds.explorer.previewTitle} style={{ fontWeight: "bold", fontSize: 13 }}>{doc.title}</span>
          {/* Confidentiality badge */}
          <span
            style={{
              display: "inline-block",
              padding: "1px 8px",
              fontSize: 10,
              fontWeight: "bold",
              color: "white",
              background: confColor,
              borderRadius: 2,
            }}
          >
            {confLabel}
          </span>
          {/* Document type badge */}
          <span
            style={{
              display: "inline-block",
              padding: "1px 8px",
              fontSize: 10,
              fontWeight: "bold",
              color: "#000",
              background: "#e0e0e0",
              border: "1px solid #808080",
              borderRadius: 2,
            }}
          >
            {doc.documentType}
          </span>
          {/* Version */}
          <span style={{ fontSize: 11, color: "#444" }}>
            v{doc.currentVersion}
          </span>
          {/* Attorney */}
          <span style={{ fontSize: 11, color: "#444" }}>
            {attorney}
          </span>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 4 }}>
          <HelpTip k="documentPreview.sendToPromptLab" wrap>
            <Win95Button
              label="Send to Prompt Lab"
              onClick={() => {
                // Phase H (H.1) — Prime a new Prompt Lab session with this
                // doc as context, then focus the Prompt Lab window.
                dispatchPromptLabPrefill(dispatch, {
                  docId: doc._id,
                  docTitle: doc.title,
                  docContent: doc.content,
                  matterId: doc.matterId,
                  // MockDocFull stores docType / conf as raw strings;
                  // the prefill accepts the enum type which is also a
                  // string at runtime, so the cast is safe.
                  documentType: doc.documentType as unknown as DocumentType,
                  confidentialityLevel:
                    doc.confidentialityLevel as unknown as ConfidentialityLevel,
                  responsibleAttorneyId: doc.responsibleAttorneyId,
                  modifiedAt: doc.modifiedAt,
                });
                // Open / focus the existing /prompt-lab window registered
                // by Win95Desktop. Falls through silently if the window
                // hasn't been registered yet (rare — happens only outside
                // the desktop shell, e.g. when previewing inside a
                // per-doc window).
                const win = windowContext.getWindow(PROMPT_LAB_PATH);
                if (!win) {
                  windowContext.openWindow(PROMPT_LAB_PATH);
                } else if (win.state === 'closed' || win.state === 'minimized') {
                  windowContext.openWindow(PROMPT_LAB_PATH);
                } else {
                  windowContext.focusWindow(PROMPT_LAB_PATH);
                }
              }}
              data-testid={testIds.explorer.sendToPromptLab}
            />
          </HelpTip>
          <HelpTip k="documentPreview.versionHistory" wrap>
            <Win95Button
              label="Version History"
              onClick={() => setVersionHistoryOpen(true)}
              data-testid={testIds.explorer.versionHistory}
            />
          </HelpTip>
          {/* K.3.a — proof-of-SharePoint affordance. Renders as a real anchor
              when the doc's externalRef.webUrl is a *.sharepoint.com URL;
              otherwise renders as a muted disabled button so evaluators
              see the absence rather than wondering why no button rendered.
              The disabled button is honest: "Fixture doc — not in SharePoint". */}
          <OpenInSharePointButton
            webUrl={doc.sharePointWebUrl ?? null}
            shareUrl={doc.publicShareUrl ?? null}
          />
          {/* Phase 6.5 — anonymous share-link generator. Only meaningful
              for real SP-backed docs. Demo workspace, so visibility is
              maximised: anyone with the URL can view, no sign-in. */}
          {isRealSharePointWebUrl(doc.sharePointWebUrl ?? null) ? (
            <HelpTip k="documentPreview.sharePublicly" wrap>
              <button
                type="button"
                data-testid={testIds.explorer.shareDocPublicly}
                onClick={() => void handleCreatePublicShare()}
                disabled={shareResult.kind === 'pending'}
                style={{
                  height: 24,
                  padding: '0 12px',
                  background: '#d0d0f0',
                  borderStyle: 'solid',
                  borderWidth: 2,
                  fontFamily: 'var(--xp-font-body)',
                  fontSize: 11,
                  fontWeight: 'bold',
                  color: '#000080',
                  cursor: shareResult.kind === 'pending' ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {shareResult.kind === 'pending'
                  ? 'Creating link…'
                  : 'Get share link'}
              </button>
            </HelpTip>
          ) : null}
        </div>
      </div>

      {/* Share-link result panel — toggles open when the user clicks
          "Share publicly". The URL is selectable, openable, copyable.
          target="_blank" + rel="noopener noreferrer" per security
          guidelines. */}
      {shareResult.kind === 'ok' ? (
        <div
          data-testid={testIds.explorer.shareDocResultPanel}
          style={{
            padding: '8px 12px',
            background: '#fffbcc',
            borderBottom: '1px solid #b08800',
            fontSize: 11,
            color: '#000',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            flexShrink: 0,
          }}
        >
          <div style={{ fontWeight: 'bold' }}>
            {shareResult.cached === true
              ? 'Existing public link (reused — still valid)'
              : 'Public link created'}
            {shareResult.scope === 'organization' ? (
              <span
                style={{ marginLeft: 6, fontWeight: 'normal', color: '#374151' }}
              >
                — tenant only allows org-scope; recipient must be signed
                into Microsoft
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="text"
              readOnly
              value={shareResult.webUrl}
              data-testid={testIds.explorer.shareDocUrlInput}
              onFocus={(e) => e.currentTarget.select()}
              style={{
                flex: 1,
                fontFamily: 'var(--xp-font-mono)',
                fontSize: 11,
                padding: '3px 6px',
                background: '#fff',
                border: '1px solid #808080',
                color: '#000',
              }}
            />
            <button
              type="button"
              data-testid={testIds.explorer.shareDocCopyButton}
              onClick={() => void handleCopyShareUrl(shareResult.webUrl)}
              style={{
                fontFamily: 'var(--xp-font-body)',
                fontSize: 11,
                padding: '3px 10px',
                background: '#ece9d8',
                borderStyle: 'solid',
                borderWidth: 1,
                color: '#000',
                cursor: 'pointer',
              }}
            >
              {shareCopied ? '✓ Copied' : 'Copy link'}
            </button>
            <a
              href={shareResult.webUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={testIds.explorer.shareDocOpenLink}
              style={{
                fontFamily: 'var(--xp-font-body)',
                fontSize: 11,
                padding: '3px 10px',
                background: '#ece9d8',
                borderStyle: 'solid',
                borderWidth: 1,
                color: '#000',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              Open ↗
            </a>
            <button
              type="button"
              onClick={() => setShareResult({ kind: 'idle' })}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 14,
                lineHeight: 1,
                cursor: 'pointer',
                color: '#000',
                padding: '0 4px',
              }}
            >
              ×
            </button>
          </div>
          <div style={{ fontSize: 10, color: '#374151' }}>
            Expires {new Date(shareResult.expiresAt).toLocaleDateString()} ·
            demo workspace, sample data, anyone with this URL can view.
          </div>
        </div>
      ) : null}
      {shareResult.kind === 'error' ? (
        <div
          data-testid={testIds.explorer.shareDocErrorPanel}
          style={{
            padding: '6px 12px',
            background: '#ffe8e8',
            borderBottom: '1px solid #cc6666',
            fontSize: 11,
            color: '#660000',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span>Could not create public link: {shareResult.reason}</span>
          <button
            type="button"
            onClick={() => setShareResult({ kind: 'idle' })}
            style={{
              marginLeft: 'auto',
              border: '1px solid #660000',
              background: 'transparent',
              color: '#660000',
              padding: '0 6px',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* Save toolbar — only mounts when the doc has a real SharePoint
          back-reference. Same shape as RemoteDriveItemPreview's K.12
          toolbar. */}
      {canEdit ? (
        <div
          style={{
            padding: "4px 10px",
            background: "#ece9d8",
            borderBottom: "1px solid #aca899",
            fontSize: 11,
            color: "#000",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <HelpTip k="documentPreview.save" wrap>
            <button
              type="button"
              data-testid={testIds.explorer.remoteDriveItemSaveButton}
              disabled={!isDirty || saving}
              onClick={() => void handleSave()}
              style={{
                border: "1px solid #404080",
                background: isDirty && !saving ? "#d0d0f0" : "#e8e8e8",
                color: isDirty && !saving ? "#000080" : "#888",
                padding: "2px 8px",
                fontSize: 11,
                cursor: !isDirty || saving ? "default" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </HelpTip>
          <button
            type="button"
            data-testid={testIds.explorer.remoteDriveItemDiscardButton}
            disabled={!isDirty || saving}
            onClick={handleDiscard}
            style={{
              border: "1px solid #808080",
              background: "#f0f0f0",
              color: "#000",
              padding: "2px 8px",
              fontSize: 11,
              cursor: !isDirty || saving ? "default" : "pointer",
              opacity: !isDirty || saving ? 0.6 : 1,
            }}
            title="Discard your changes and restore the SharePoint version."
          >
            Discard
          </button>
          {isDirty ? (
            <span style={{ color: "#5a3e00", fontSize: 10 }}>
              ● Unsaved changes
            </span>
          ) : null}
          {saveSuccess ? (
            <span
              data-testid={testIds.explorer.remoteDriveItemSaveSuccessMessage}
              style={{ marginLeft: "auto", color: "#0a5a0a", fontSize: 10 }}
            >
              ✓ Saved to SharePoint
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Save error panel — sticky until retry or discard. */}
      {canEdit && saveError !== null ? (
        <div
          data-testid={testIds.explorer.remoteDriveItemSaveErrorPanel}
          style={{
            padding: "6px 10px",
            background: "#ffe8e8",
            borderBottom: "1px solid #cc6666",
            fontSize: 11,
            color: "#660000",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span>{saveError}</span>
          <HelpTip k="documentPreview.saveError.dismiss" wrap>
            <button
              type="button"
              onClick={() => setSaveError(null)}
              style={{
                marginLeft: "auto",
                border: "1px solid #660000",
                background: "transparent",
                color: "#660000",
                padding: "0 6px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Dismiss
            </button>
          </HelpTip>
        </div>
      ) : null}

      {/* Content area — TipTap when editable, read-only <pre> otherwise. */}
      {canEdit && draft !== null ? (
        <div
          data-testid={testIds.explorer.previewContent}
          style={{
            flex: 1,
            overflow: "auto",
            background: "white",
            color: "#000",
          }}
        >
          {/* F-DOCX-ROUNDTRIP-WIRING Phase 4 — sidecar chip + error banner. */}
          {sidecarBundle &&
          (sidecarBundle.comments.length > 0 ||
            sidecarBundle.revisions.length > 0 ||
            sidecarBundle.footnotes.length > 0) ? (
            <div
              data-testid="docx-sidecar-chip"
              title="These will be preserved when you save back to SharePoint."
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                margin: '6px 8px 0',
                padding: '3px 8px',
                background: '#fffde7',
                border: '1px solid #d4c97a',
                borderRadius: 4,
                fontSize: 11,
                fontFamily: 'var(--xp-font-body)',
                color: '#5b4500',
                width: 'fit-content',
              }}
            >
              📎 Sidecars:
              {sidecarBundle.comments.length > 0 ? (
                <span>{sidecarBundle.comments.length} comment{sidecarBundle.comments.length === 1 ? '' : 's'}</span>
              ) : null}
              {sidecarBundle.revisions.length > 0 ? (
                <span>· {sidecarBundle.revisions.length} revision{sidecarBundle.revisions.length === 1 ? '' : 's'}</span>
              ) : null}
              {sidecarBundle.footnotes.length > 0 ? (
                <span>· {sidecarBundle.footnotes.length} footnote{sidecarBundle.footnotes.length === 1 ? '' : 's'}</span>
              ) : null}
            </div>
          ) : null}
          {sidecarExtractError !== null ? (
            <div
              data-testid="docx-sidecar-extract-error"
              style={{
                margin: '6px 8px 0',
                padding: '6px 10px',
                background: '#fff3cd',
                border: '1px solid #d4ba3f',
                fontSize: 11,
                fontFamily: 'var(--xp-font-body)',
                color: '#5b4500',
              }}
            >
              ⚠ Sidecars couldn't be extracted from this docx ({sidecarExtractError}).
              Edits will save back as markdown only — comments, tracked
              changes, and footnotes won't be preserved.
            </div>
          ) : null}
          <PreviewTipTapEditor
            value={draft}
            onChange={(md) => setDraft(md)}
            sidecars={
              sidecarBundle
                ? {
                    // Strip Convex _id/_creationTime; the editor's
                    // DocxSidecar* shapes only carry the render-side
                    // fields (offsets + text + author + date).
                    comments: sidecarBundle.comments.map((c) => ({
                      docxCommentId: c.docxCommentId,
                      authorName: c.authorName,
                      date: c.date,
                      commentText: c.commentText,
                      startOffset: c.startOffset,
                      endOffset: c.endOffset,
                    })),
                    revisions: sidecarBundle.revisions.map((r) => ({
                      docxRevisionId: r.docxRevisionId,
                      type: r.type,
                      authorName: r.authorName,
                      date: r.date,
                      text: r.text,
                      startOffset: r.startOffset,
                      endOffset: r.endOffset,
                    })),
                    footnotes: sidecarBundle.footnotes.map((f) => ({
                      docxFootnoteId: f.docxFootnoteId,
                      noteNumber: f.noteNumber,
                      content: f.content,
                      refOffset: f.refOffset,
                    })),
                  }
                : undefined
            }
            onFootnoteClick={(fn) =>
              setFootnoteDrawerOpen({
                open: true,
                content: fn.content,
                noteNumber: fn.noteNumber,
              })
            }
          />
          {footnoteDrawerOpen.open ? (
            <div
              data-testid={testIds.explorer.previewFootnoteDrawer}
              style={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                maxWidth: 320,
                background: '#fffde7',
                border: '1px solid #f9a825',
                borderRadius: 3,
                padding: '8px 10px',
                fontSize: 11,
                lineHeight: 1.4,
                color: '#000',
                boxShadow: '2px 2px 0 0 #80807060',
                zIndex: 5,
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                Footnote {footnoteDrawerOpen.noteNumber}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {footnoteDrawerOpen.content}
              </div>
              <button
                type="button"
                onClick={() =>
                  setFootnoteDrawerOpen({ open: false, content: '', noteNumber: 0 })
                }
                style={{
                  marginTop: 6,
                  padding: '2px 6px',
                  fontSize: 10,
                  background: '#ece9d8',
                  border: '1px solid #aca899',
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          data-testid={testIds.explorer.previewContent}
          style={{
            flex: 1,
            overflow: "auto",
            padding: "8px 12px",
            background: "white",
            whiteSpace: "pre-wrap",
            fontFamily: "var(--xp-font-mono)",
            fontSize: 12,
            lineHeight: 1.5,
            color: "#000",
          }}
        >
          {doc.content
            ? doc.content
            : hydrating
              ? "Fetching content from SharePoint…"
              : hydrateError
                ? `(No content — ${hydrateError})`
                : "(No content)"}
        </div>
      )}

      {/* Phase E — per-doc provenance footer. Source pill + live badge +
          last-modified + author. Hides nothing — every doc gets one. */}
      <DocumentProvenance
        source={resolveProvenanceSource(doc, live !== undefined && live !== null)}
        lastModifiedISO={doc.modifiedAt}
        authorDisplayName={attorney}
        sharePointWebUrl={doc.sharePointWebUrl ?? null}
      />

      {/* Phase H (H.2) — version history dialog. Mounted next to the
          preview so close-on-overlay-click doesn't traverse the
          provenance footer. */}
      <VersionHistoryDialog
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        documentId={doc._id}
        documentTitle={doc.title}
      />
    </div>
  );
}

/**
 * Pick the right provenance pill given the live row + fixture fallback.
 * Exported so the tests can pin behaviour.
 */
export function resolveProvenanceSource(
  doc: MockDocFull,
  isLive: boolean,
): ProvenanceSource {
  // Live SharePoint doc — has a webUrl / source='sharepoint'.
  if (doc.source === 'sharepoint' || (doc.sharePointWebUrl && doc.sharePointWebUrl.length > 0)) {
    return 'sharepoint';
  }
  // Local-authored live doc (Convex but no SP linkage).
  if (isLive && doc.source === 'local') {
    return 'vault';
  }
  // Fixture-only fallback (mockDocsFull / no live row).
  return 'fixture';
}

// ---------------------------------------------------------------------------
// Win95 button helper
// ---------------------------------------------------------------------------

function Win95Button({ label, onClick, "data-testid": dataTestId }: { label: string; onClick: () => void; "data-testid"?: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={dataTestId}
      style={{
        height: 24,
        padding: "0 12px",
        background: "#ece9d8",
        borderStyle: "solid",
        borderWidth: 2,
        fontFamily: "var(--xp-font-body)",
        fontSize: 11,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

/**
 * K.3.a — Open this doc in SharePoint Online.
 *
 * Renders an anchor (`<a>`) when there's a usable SharePoint URL.
 * `rel="noopener noreferrer"` is REQUIRED on every `target="_blank"` link
 * for security (tabnabbing + referrer leak). Renders a disabled button
 * with explanatory title when the doc is a fixture without a real
 * SharePoint backing — evaluators see the affordance and learn the doc
 * isn't backed by SharePoint, instead of wondering why no button rendered.
 *
 * URL resolution (Phase A.1, 2026-05-27):
 *   1. `shareUrl` (anonymous Graph createLink URL, minted at provision
 *      time by `mintFixtureShareLinksForSlug`) — preferred. Carries its
 *      own bearer token in the path; opens with no sign-in.
 *   2. `webUrl` (raw driveItem.webUrl) — fallback. Requires tenant
 *      membership. Sandbox-tenant docs (areweai) bounce non-members to
 *      AccessDenied.aspx, so this branch is only correct for users we
 *      know are signed in to the right tenant.
 *
 * Rationale: J.3 "private workspace" users never authenticate to
 * areweai, so they MUST get the anonymous URL or the button is a
 * footgun. F18 customer-tenant users may not have a share-link minted
 * yet — they fall back to `webUrl` which works because they're tenant
 * members.
 *
 * Exported via the named export of the parent module so tests can mount
 * it directly without a full DocumentPreview tree.
 */
export function OpenInSharePointButton({
  webUrl,
  shareUrl,
}: {
  webUrl: string | null | undefined;
  shareUrl?: string | null | undefined;
}) {
  // Prefer the anonymous share-link (Phase A.1). Falls back to the raw
  // driveItem webUrl when no link is on file (real customer tenants,
  // pre-Phase-A.1 portals).
  const preferredUrl =
    isRealSharePointWebUrl(shareUrl) ? shareUrl : webUrl;
  const real = isRealSharePointWebUrl(preferredUrl);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const baseStyle: React.CSSProperties = {
    height: 24,
    padding: '0 12px',
    background: '#ece9d8',
    borderStyle: 'solid',
    borderWidth: 2,
    fontFamily: 'var(--xp-font-body)',
    fontSize: 11,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    textDecoration: 'none',
    color: real ? '#000' : '#374151',
  };
  if (real && typeof preferredUrl === 'string') {
    return (
      <a
        href={preferredUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testIds.explorer.openInSharePoint}
        title="Open this document in SharePoint Online"
        aria-label="Open in SharePoint"
        style={{ ...baseStyle, cursor: 'pointer' }}
      >
        Open in SharePoint <span aria-hidden>↗</span>
      </a>
    );
  }
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <button
        type="button"
        data-testid={testIds.explorer.openInSharePoint}
        disabled
        title="Sample document — not backed by a real SharePoint item"
        aria-label="Open in SharePoint (unavailable — sample document)"
        style={{
          ...baseStyle,
          cursor: 'not-allowed',
          opacity: 0.55,
        }}
      >
        Open in SharePoint <span aria-hidden>↗</span>
      </button>
      <span
        style={{
          fontSize: 10,
          padding: '1px 5px',
          background: '#fffbcc',
          border: '1px solid #b08800',
          borderRadius: 2,
          color: '#000',
          fontFamily: 'var(--xp-font-body)',
        }}
        data-testid={testIds.explorer.openInSharePointBadge}
      >
        sample
      </span>
      <button
        type="button"
        onClick={() => setExplainerOpen((v) => !v)}
        aria-label="Why is Open in SharePoint unavailable?"
        aria-expanded={explainerOpen}
        data-testid={testIds.explorer.openInSharePointExplain}
        style={{
          width: 18,
          height: 18,
          padding: 0,
          background: '#ece9d8',
          borderStyle: 'solid',
          borderWidth: 1,
          fontFamily: 'var(--xp-font-body)',
          fontSize: 11,
          fontWeight: 'bold',
          color: '#000',
          cursor: 'pointer',
          lineHeight: 1,
        }}
      >
        ?
      </button>
      {explainerOpen && (
        <div
          role="dialog"
          aria-label="Open in SharePoint — explanation"
          data-testid={testIds.explorer.openInSharePointExplainer}
          style={{
            position: 'absolute',
            top: 28,
            right: 0,
            zIndex: 50,
            width: 320,
            padding: 10,
            background: '#ffffe1',
            border: '1px solid #b08800',
            boxShadow: '2px 2px 6px rgba(0,0,0,0.25)',
            fontFamily: 'var(--xp-font-body)',
            fontSize: 11,
            color: '#000',
            lineHeight: 1.4,
          }}
        >
          <strong>This is a sample document.</strong> It lives only in
          the Repancy demo workspace and isn&apos;t backed by a real
          SharePoint file yet, so there&apos;s nothing to open in
          SharePoint Online.
          <div style={{ marginTop: 6 }}>
            To get a real SharePoint document: use{' '}
            <strong>Import from SharePoint…</strong> to link an existing
            file, or use <strong>Bulk import…</strong> to migrate a
            folder, or provision a fresh portal via{' '}
            <strong>Set up SharePoint</strong> on the desktop.
          </div>
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => setExplainerOpen(false)}
              style={{
                fontFamily: 'var(--xp-font-body)',
                fontSize: 11,
                padding: '2px 10px',
                background: '#ece9d8',
                borderStyle: 'solid',
                cursor: 'pointer',
                color: '#000',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
